import React, { useState, useEffect, useCallback } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle2, Download, Lock, Unlock, Loader2, ListChecks } from "lucide-react";
import { apiGet, apiPost, apiDownload } from "../igualdad-api.js";
import { useIgualdad, puede } from "../igualdad-store.jsx";
import { Status, SectionHead } from "../igualdad-components.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Consolidación del Registro
   Semáforo de las nueve reglas transversales del §6, panel de
   incidencias filtrable con su exportación, y cierre o
   reapertura del periodo. Las tres lecturas salen del mismo
   ValidadorCierre del backend, así que no pueden discrepar.
   ============================================================ */

const ETIQUETA_REGLA = {
  solapamiento: "Solapamientos",
  descuadre: "Descuadres de importe",
  completitud: "Completitud",
  vigencia: "Vigencia de conceptos",
  coherencia_temporal: "Coherencia temporal",
  unicidad: "Unicidad de identificadores",
  hueco: "Huecos de cobertura",
  trazabilidad: "Sin lote de origen",
  sin_situaciones: "Personas sin situaciones",
};

const TIPOS = Object.keys(ETIQUETA_REGLA);
const SEVERIDADES = [["error", "Error"], ["aviso", "Aviso"]];

/* Icono y tono de una regla según su severidad y su recuento. */
function tonoRegla(regla) {
  if (!regla.n) return { Icono: CheckCircle2, color: "var(--success)" };
  if (regla.severidad === "error") return { Icono: AlertTriangle, color: "var(--danger)" };
  return { Icono: AlertTriangle, color: "#d9682e" };
}

function IgualdadConsolidacion() {
  const { session, fireToast } = useIgualdad();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validacion, setValidacion] = useState(null);

  const [incidencias, setIncidencias] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [ultima, setUltima] = useState(1);
  const [tipo, setTipo] = useState("");
  const [severidad, setSeveridad] = useState("");

  const [cerrando, setCerrando] = useState(false);

  const permisos = session.user?.permisos || [];
  const esStaff = Boolean(session.user?.es_staff);
  const puedeCerrar = puede("politicas.cerrar", permisos, esStaff);

  const cerrado = session.politica?.estado === "cerrado";
  const apto = Boolean(validacion?.apto);
  const reglas = validacion?.reglas || [];
  const nErrores = reglas.filter((r) => r.severidad === "error").reduce((a, r) => a + r.n, 0);

  const cargarValidacion = useCallback(async () => {
    if (!session.politicaId) { setLoading(false); setValidacion(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiGet(`/politicas-retributivas/${session.politicaId}/validacion-cierre`);
      setValidacion(res.data || null);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }, [session.politicaId]);

  const cargarIncidencias = useCallback(async () => {
    if (!session.politicaId) { setIncidencias([]); setTotal(0); setUltima(1); return; }
    try {
      const params = { per_page: 50, page: pagina };
      if (tipo) params.tipo = tipo;
      if (severidad) params.severidad = severidad;
      const res = await apiGet(`/politicas-retributivas/${session.politicaId}/incidencias`, params);
      setIncidencias(res.data || []);
      setTotal(res.meta?.total ?? (res.data || []).length);
      setUltima(res.meta?.last_page ?? 1);
    } catch (e) { setIncidencias([]); setTotal(0); setUltima(1); }
  }, [session.politicaId, tipo, severidad, pagina]);

  useEffect(() => { cargarValidacion(); }, [cargarValidacion]);
  useEffect(() => { cargarIncidencias(); }, [cargarIncidencias]);

  async function cerrar() {
    if (cerrando) return;
    setCerrando(true);
    try {
      await apiPost(`/politicas-retributivas/${session.politicaId}/cerrar`);
      fireToast("Registro cerrado. Las situaciones y los importes quedan congelados.");
      await cargarValidacion();
    } catch (e) {
      fireToast(e.message || "No se pudo cerrar el Registro.");
      await cargarValidacion();
    } finally { setCerrando(false); }
  }

  async function reabrir() {
    if (cerrando) return;
    setCerrando(true);
    try {
      await apiPost(`/politicas-retributivas/${session.politicaId}/reabrir`);
      fireToast("Registro reabierto.");
      await cargarValidacion();
    } catch (e) {
      fireToast(e.message || "No se pudo reabrir el Registro.");
    } finally { setCerrando(false); }
  }

  function exportar() {
    const params = {};
    if (tipo) params.tipo = tipo;
    if (severidad) params.severidad = severidad;
    apiDownload(
      `/politicas-retributivas/${session.politicaId}/incidencias/export`,
      params,
      "incidencias.xlsx",
    );
  }

  if (!session.politicaId) {
    return (
      <div className="rb-page">
        <h1 className="rb-h1">Consolidación del Registro</h1>
        <Status empty emptyText="Selecciona una política retributiva para consolidar su Registro." />
      </div>
    );
  }

  return (
    <div className="rb-page">
      <h1 className="rb-h1">Consolidación del Registro</h1>

      <div className="rb-card">
        <SectionHead
          icon={ShieldCheck}
          title={cerrado ? "Registro cerrado" : "Registro en borrador"}
          right={puedeCerrar ? (
            cerrado ? (
              <button className="rb-btn-ghost" disabled={cerrando} onClick={reabrir}>
                {cerrando ? <Loader2 size={14} className="rb-spin" /> : <Unlock size={14} />} Reabrir
              </button>
            ) : (
              <button className="rb-btn" disabled={!apto || cerrando} onClick={cerrar}>
                {cerrando ? <Loader2 size={14} className="rb-spin" /> : <Lock size={14} />} Cerrar Registro
              </button>
            )
          ) : null}
        />

        {loading || error ? <Status loading={loading} error={error} /> : (
          <>
            <div className="rb-reglas">
              {reglas.map((r) => {
                const { Icono, color } = tonoRegla(r);
                return (
                  <div key={r.regla} className="rb-regla" data-testid={`regla-${r.regla}`}>
                    <Icono size={15} color={color} />
                    <span className="rb-regla-l">{ETIQUETA_REGLA[r.regla] || r.regla}</span>
                    <b style={{ color }}>{r.n}</b>
                  </div>
                );
              })}
            </div>
            {!apto && !cerrado ? (
              <p className="rb-note">
                {nErrores} {nErrores === 1 ? "incidencia bloqueante impide" : "incidencias bloqueantes impiden"} cerrar el
                Registro. Los avisos no bloquean.
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="rb-card rb-card-flat" style={{ marginTop: 18 }}>
        <div style={{ padding: "14px 16px" }}>
          <SectionHead
            icon={ListChecks}
            title={`Incidencias · ${total}`}
            right={<button className="rb-btn-ghost" onClick={exportar}><Download size={14} /> Exportar XLSX</button>}
          />
          <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
            <div>
              <label htmlFor="filtro-tipo">Tipo</label>
              <select id="filtro-tipo" className="rb-inp" value={tipo}
                onChange={(e) => { setPagina(1); setTipo(e.target.value); }}>
                <option value="">Todos</option>
                {TIPOS.map((t) => <option key={t} value={t}>{ETIQUETA_REGLA[t]}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="filtro-severidad">Severidad</label>
              <select id="filtro-severidad" className="rb-inp" value={severidad}
                onChange={(e) => { setPagina(1); setSeveridad(e.target.value); }}>
                <option value="">Todas</option>
                {SEVERIDADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>

        <table className="rb-table">
          <thead><tr>
            <th className="rb-l">Tipo</th><th className="rb-l">Severidad</th>
            <th className="rb-l">Persona</th><th className="rb-l">Situación</th><th className="rb-l">Detalle</th>
          </tr></thead>
          <tbody>
            {incidencias.map((i, n) => (
              <tr key={`${i.tipo}-${i.situacion_id}-${n}`}>
                <td className="rb-l">{ETIQUETA_REGLA[i.tipo] || i.tipo}</td>
                <td className="rb-l">
                  <span className="rb-chip" style={{
                    background: i.severidad === "error" ? "var(--danger-bg)" : "var(--orange-50)",
                    color: i.severidad === "error" ? "var(--danger-700)" : "var(--brand-hover)",
                  }}>{i.severidad === "error" ? "Error" : "Aviso"}</span>
                </td>
                <td className="rb-l rb-mono">{i.persona || "—"}</td>
                <td className="rb-l rb-mono">{i.situacion_id ? `#${i.situacion_id}` : "—"}</td>
                <td className="rb-l rb-soft">{i.detalle}</td>
              </tr>
            ))}
            {!incidencias.length ? (
              <tr><td className="rb-l rb-soft" colSpan={5}>Sin incidencias con los filtros aplicados.</td></tr>
            ) : null}
          </tbody>
        </table>

        {ultima > 1 ? (
          <div className="rb-tablefoot" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Página {pagina} de {ultima} · {total} incidencias</span>
            <span style={{ display: "flex", gap: 8 }}>
              <button className="rb-btn-ghost" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</button>
              <button className="rb-btn-ghost" disabled={pagina >= ultima} onClick={() => setPagina((p) => p + 1)}>Siguiente</button>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { IgualdadConsolidacion };
